<template>
    <div
        class="tab-group-wrapper w-100"
        :class="{
            /* fade only on the left side */
            'overlay-start': overflowLeft && !overflowRight,

            /* fade only on the right side */
            'overlay-end': !overflowLeft && overflowRight,

            /* fade on both sides */
            'overlay-both': overflowLeft && overflowRight,
        }"
    >
        <draggable
            v-if="draggable"
            ref="container"
            v-model="internalTabs"
            class="tab-group w-100"
            item-key="value"
            :component="'div'"
            :ghost-class="'ghost'"
            :disabled="readonly"
            @end="onReorder"
        >
            <template #item="{ element: tab }">
                <button
                    :key="tab.value"
                    type="button"
                    :class="['tab', { selected: tab.value === modelValue }]"
                    :disabled="readonly"
                    @click="selectTab(tab.value)"
                >
                    <span
                        v-if="!tab.isEditing || !canEdit"
                        @dblclick="editTabName(tab)"
                        @longPress="editTabName(tab)"
                    >
                        {{ localizeInfectionPreventionTerminology(tab.text) }}
                    </span>
                    <input
                        v-else
                        v-model="tab.text"
                        class="tab"
                        :ref="(el) => (tab.ref = el)"
                        @blur="finishEditing(tab)"
                        @keyup.enter="finishEditing(tab)"
                        style="width: 100px"
                    />
                </button>
            </template>
            <template #footer>
                <div
                    class="flex-grow-1"
                    style="
                        border-bottom: 0.125rem solid var(--neutral-600);
                        min-width: 0;
                        flex: 1 1 auto;
                    "
                ></div>
            </template>
        </draggable>

        <div v-else ref="container" class="tab-group w-100">
            <button
                v-for="tab in internalTabs"
                :key="tab.value"
                type="button"
                :class="['tab', { selected: tab.value === modelValue }]"
                :disabled="readonly"
                @click="selectTab(tab.value)"
            >
                <div
                    v-if="!tab.isEditing || !canEdit"
                    class="d-flex gap-2 align-items-center"
                >
                    <i
                        v-if="tab.hasError"
                        class="fi fi-alert-circle"
                        style="color: var(--danger)"
                    ></i>
                    <span @dblclick="editTabName(tab)">
                        {{ localizeInfectionPreventionTerminology(tab.text) }}
                    </span>
                </div>
                <input
                    v-else
                    v-model="tab.text"
                    class="tab"
                    :ref="(el) => (tab.ref = el)"
                    @blur="finishEditing(tab)"
                    @keyup.enter="finishEditing(tab)"
                    style="width: 100px"
                />
            </button>
            <div
                class="flex-grow-1"
                style="
                    border-bottom: 0.125rem solid var(--neutral-600);
                    min-width: 0;
                    flex: 1 1 auto;
                "
            ></div>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable';

export default {
    name: 'TabGroup',
    components: {
        draggable,
    },
    props: {
        tabs: {
            type: Array,
            required: true,
        },
        modelValue: {
            type: String,
            default: '',
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        canEdit: {
            type: Boolean,
            default: false,
        },
        draggable: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            internalTabs: [...this.tabs],
            resizeObserver: null,
            overflowLeft: false, // there is hidden content on the left
            overflowRight: false, // there is hidden content on the right
            container: null,
        };
    },
    watch: {
        tabs: {
            handler(newTabs) {
                this.internalTabs = [...newTabs];
            },
            immediate: true,
            deep: true,
        },
        modelValue: {
            immediate: true,
            handler(value) {
                this.$nextTick(() => {
                    const c = this.container;
                    if (!c) return;

                    const selected = c.querySelector('.tab.selected');

                    if (!selected) return;

                    const getCentreScroll = () => {
                        const left = selected.offsetLeft;
                        const width = selected.offsetWidth;
                        const pos = left - c.offsetWidth / 2 + width / 2;
                        const maxLeft = c.scrollWidth - c.offsetWidth;
                        return Math.max(0, Math.min(pos, maxLeft));
                    };

                    c.scrollTo({ left: getCentreScroll(), behavior: 'smooth' });

                    this.updateOverflow();
                });
            },
        },
        internalTabs: {
            deep: true,
            handler(tabs) {
                for (const tab of tabs) {
                    if (
                        tab.isEditing &&
                        tab.ref &&
                        typeof tab.ref.focus === 'function'
                    ) {
                        this.$nextTick(() => {
                            tab.ref.focus();
                        });
                    }
                }
            },
        },
    },
    methods: {
        selectTab(value) {
            if (!this.readonly) {
                requestAnimationFrame(() => {
                    this.$emit('update:modelValue', value);
                    this.$emit('change', value);
                });
            }
        },

        /* sets overflowLeft / overflowRight */
        updateOverflow() {
            if (!this.container) return;

            const c = this.container;
            const atStart = c.scrollLeft === 0;
            const atEnd =
                Math.ceil(c.scrollLeft + c.clientWidth) >= c.scrollWidth;

            this.overflowLeft = !atStart;
            this.overflowRight = !atEnd;
        },
        editTabName(tab) {
            tab.isEditing = true;
        },
        finishEditing(tab) {
            if (!tab.text) {
                tab.text = 'Untitled';
            }
            tab.isEditing = false;
        },
        onReorder() {
            this.$emit('update:tabs', [...this.internalTabs]);
        },
    },

    mounted() {
        this.container = this.draggable
            ? this.$refs.container.$el
            : this.$refs.container;

        // initial calculation
        this.updateOverflow();

        /* recalc while the user scrolls */
        this.container.addEventListener('scroll', this.updateOverflow, {
            passive: true,
        });

        /* recalc whenever the container is resized */
        this.resizeObserver = new ResizeObserver(this.updateOverflow);
        this.resizeObserver.observe(this.container);
    },
    beforeUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.container) {
            this.container.removeEventListener('scroll', this.updateOverflow);
        }
    },
};
</script>

<style scoped>
.tab-group-wrapper {
    position: relative;
    display: inline-flex;
    max-width: 100%;
    overflow: visible;
    box-sizing: border-box;
}

.tab-group {
    display: inline-flex;
    max-width: 100%;
    overflow-x: auto;
    scroll-behavior: smooth;
    position: relative;
}

.tab-group::-webkit-scrollbar {
    display: none;
}

.tab-group-wrapper::before,
.tab-group-wrapper::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 4.375rem;
    height: 100%;
    pointer-events: none;
    z-index: 1;
}

.tab {
    padding: var(--space-1) var(--space-3) 0.375rem var(--space-3);
    border: none;
    background-color: transparent;
    color: var(--neutral-800);
    border-bottom: 0.125rem solid var(--neutral-600);
    cursor: pointer;
    flex-shrink: 0;
    vertical-align: middle;
    scroll-snap-align: center;
    box-sizing: border-box;
    position: relative;
}

.tab:not(:disabled):hover {
    color: var(--primary-500);
    transition: all 0.2s ease-in-out;
    position: relative;
}

.tab:focus {
    outline: none;
}

.tab:focus-visible {
    outline: auto;
    outline-color: var(--success) !important;
}

.tab.selected {
    color: var(--primary-500);
    z-index: 2;
    border-bottom: 0.125rem solid var(--primary-300);
}

.tab:disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

.editable-textarea {
    width: 100%;
    min-height: 0.875rem;
    /* sets the one-line height to match the static label */
    resize: none;
    border: none;
    padding: 0;
    margin: 0;
    outline: none;
    font-size: 0.875rem;
    font-weight: 550;
    line-height: 100%;
    /* match this to how tall your static label appears */
    color: var(--Colours-Neutral-900, #474747);
    background: transparent;
    overflow: hidden;
}

@media (max-width: 767.98px) {
    .overlay-start::before,
    .overlay-both::before {
        background: linear-gradient(
            90deg,
            var(--white) 27%,
            rgba(247, 247, 247, 0)
        );
        left: 0;
    }

    .overlay-end::after,
    .overlay-both::after {
        background: linear-gradient(
            270deg,
            var(--white) 27%,
            rgba(247, 247, 247, 0)
        );
        right: 0;
    }
}
</style>
